import React, { useState } from 'react';
import {
    CheckCircle, Circle, Target, Mic, User, Calendar,
    ChevronDown, ChevronRight, MoreVertical, Archive,
    Edit, Trash2, Plus, Loader2
} from 'lucide-react';
import CreateTaskModal from './CreateTaskModal';
import EditTaskModal from './EditTaskModal';

const TaskItem = ({
    task,
    toggleTask,
    expandedTasks,
    toggleTaskExpansion,
    taskMenuOpen,
    setTaskMenuOpen,
    breakdownTask,
    breakingDownTaskId,
    deleteTask,
    setTasks,
    getPriorityColor,
    onTaskUpdated,
    projects = [],
    onAssignItemProject
}) => {
    const menuRef = React.useRef(null);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setTaskMenuOpen(null);
            }
        };

        if (taskMenuOpen === task.id) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [taskMenuOpen, task.id, setTaskMenuOpen]);
    return (
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-300 relative">
            <div className="flex items-start space-x-4">
                <button
                    onClick={() => toggleTask(task.id)}
                    className="mt-1 hover:scale-110 transition-transform duration-200"
                >
                    {task.completed ? (
                        <CheckCircle className="w-6 h-6 text-green-500" />
                    ) : (
                        <Circle className="w-6 h-6 text-gray-400 hover:text-gray-600" />
                    )}
                </button>

                <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                            {task.subtasks && task.subtasks.length > 0 && (
                                <button
                                    onClick={() => toggleTaskExpansion(task.id)}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                    {expandedTasks.has(task.id) ? (
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-500" />
                                    )}
                                </button>
                            )}
                            <span className={`font-semibold text-lg ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                {task.text}
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                            </span>
                            <div className="relative z-10" ref={menuRef}>
                                <button
                                    onClick={() => setTaskMenuOpen(taskMenuOpen === task.id ? null : task.id)}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                    <MoreVertical className="w-4 h-4 text-gray-500" />
                                </button>

                                {taskMenuOpen === task.id && (
                                    <div className="absolute right-0 bottom-4 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999] min-w-[140px] max-h-48 overflow-y-auto">
                                        <button
                                            onClick={() => {
                                                if (breakingDownTaskId === task.id) return;
                                                breakdownTask(task.id, task.text);
                                            }}
                                            disabled={breakingDownTaskId === task.id}
                                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center"
                                        >
                                            {breakingDownTaskId === task.id ? (
                                                <>
                                                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                                    Breaking down...
                                                </>
                                            ) : (
                                                <>
                                                    <Archive className="w-3 h-3 mr-2" />
                                                    Break Down
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => { 
                                                setTaskMenuOpen(null);
                                                onTaskUpdated(task);
                                            }}
                                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center"
                                        >
                                            <Edit className="w-3 h-3 mr-2" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteTask(task.id)}
                                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center text-red-600"
                                        >
                                            <Trash2 className="w-3 h-3 mr-2" />
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                        {task.assignee && (
                            <span className="flex items-center bg-gray-100/80 px-3 py-1 rounded-lg">
                                <User className="w-4 h-4 mr-2" />
                                {task.assignee}
                            </span>
                        )}
                        {task.dueDate && (
                            <span className="flex items-center bg-gray-100/80 px-3 py-1 rounded-lg">
                                <Calendar className="w-4 h-4 mr-2" />
                                Due {new Date(task.dueDate).toLocaleDateString()}
                            </span>
                        )}
                        {/* Task Source Information */}
                        <span className={`flex items-center px-3 py-1 rounded-lg text-xs ${
                            task.source === 'manual' 
                                ? 'bg-blue-100/80 text-blue-700' 
                                : 'bg-green-100/80 text-green-700'
                        }`}>
                            {task.source === 'manual' ? (
                                <>
                                    <Plus className="w-3 h-3 mr-1" />
                                    Manual Task
                                </>
                            ) : (
                                <>
                                    <Mic className="w-3 h-3 mr-1" />
                                    {task.recordingCategory ? `${task.recordingCategory} Recording` : 'AI Generated'}
                                    {task.recordingDate && (
                                        <span className="ml-1">• {new Date(task.recordingDate).toLocaleDateString()}</span>
                                    )}
                                </>
                            )}
                        </span>
                        {onAssignItemProject && projects.length > 0 && (
                            <label className="inline-flex items-center gap-1 text-xs">
                                <span className="text-gray-500">Project</span>
                                <select
                                    className="border border-gray-200 rounded-lg px-2 py-0.5 bg-white text-gray-800 max-w-[10rem]"
                                    value={task.projectId || ''}
                                    onChange={(e) =>
                                        onAssignItemProject('task', task.id, e.target.value || 'unassigned')
                                    }
                                >
                                    <option value="">None</option>
                                    {projects.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>

                    {/* Subtasks */}
                    {task.subtasks && task.subtasks.length > 0 && expandedTasks.has(task.id) && (
                        <div className="ml-6 space-y-2 pt-3 border-t border-gray-200">
                            {task.subtasks.map((subtask, index) => (
                                <div key={subtask.id || `${task.id}-subtask-${index}`} className="flex items-center space-x-3">
                                    <button
                                        onClick={() => {
                                            setTasks(prev => prev.map(t =>
                                                t.id === task.id
                                                    ? {
                                                        ...t,
                                                        subtasks: t.subtasks.map(st =>
                                                            st.id === subtask.id
                                                                ? { ...st, completed: !st.completed }
                                                                : st
                                                        )
                                                    }
                                                    : t
                                            ));
                                        }}
                                        className="hover:scale-110 transition-transform duration-200"
                                    >
                                        {subtask.completed ? (
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                        ) : (
                                            <Circle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                        )}
                                    </button>
                                    <span className={`text-sm ${subtask.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                        {subtask.text}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TasksTab = ({
    tasks,
    setTasks,
    selectedFilter,
    setSelectedFilter,
    expandedTasks,
    toggleTaskExpansion,
    taskMenuOpen,
    setTaskMenuOpen,
    toggleTask,
    breakdownTask,
    breakingDownTaskId,
    deleteTask,
    getPriorityColor,
    setActiveTab,
    projects = [],
    onAssignItemProject
}) => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);

    const handleTaskCreated = (newTask) => {
        setTasks(prev => [newTask, ...prev]);
    };

    const handleTaskUpdated = (updatedTask) => {
        setTasks(prev => prev.map(task => 
            task.id === updatedTask.id ? updatedTask : task
        ));
    };

    const handleEditTask = (task) => {
        setEditingTask(task);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
                <div className="flex items-center space-x-3 flex-wrap">
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl hover:from-purple-700 hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Task
                    </button>
                    <select
                        value={selectedFilter}
                        onChange={(e) => setSelectedFilter(e.target.value)}
                        className="px-4 py-2 bg-white/80 backdrop-blur border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                        <option value="all">All Tasks</option>
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="high">High Priority</option>
                    </select>
                </div>
            </div>

            <div className="space-y-4">
                {tasks
                    .filter(task => {
                        if (selectedFilter === 'pending') return !task.completed;
                        if (selectedFilter === 'completed') return task.completed;
                        if (selectedFilter === 'high') return task.priority === 'high';
                        return true;
                    })
                    .map(task => (
                        <TaskItem
                            key={task.id}
                            task={task}
                            toggleTask={toggleTask}
                            expandedTasks={expandedTasks}
                            toggleTaskExpansion={toggleTaskExpansion}
                            taskMenuOpen={taskMenuOpen}
                            setTaskMenuOpen={setTaskMenuOpen}
                            breakdownTask={breakdownTask}
                            breakingDownTaskId={breakingDownTaskId}
                            deleteTask={deleteTask}
                            setTasks={setTasks}
                            getPriorityColor={getPriorityColor}
                            onTaskUpdated={handleEditTask}
                            projects={projects}
                            onAssignItemProject={onAssignItemProject}
                        />
                    ))}
            </div>

            {tasks.length === 0 && (
                <div className="text-center py-16">
                    <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Target className="w-12 h-12 text-orange-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">No tasks yet</h3>
                    <p className="text-gray-600 mb-8">Create your first task or start recording to generate AI tasks</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-200 font-medium shadow-lg text-lg"
                        >
                            <Plus className="w-6 h-6 mr-3" />
                            Create Task
                        </button>
                        <button
                            onClick={() => setActiveTab('knowledgeCapture')}
                            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg text-lg"
                        >
                            <Mic className="w-6 h-6 mr-3" />
                            Start Recording
                        </button>
                    </div>
                </div>
            )}

            <CreateTaskModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onTaskCreated={handleTaskCreated}
            />
            <EditTaskModal
                isOpen={!!editingTask}
                onClose={() => setEditingTask(null)}
                task={editingTask}
                onTaskUpdated={handleTaskUpdated}
            />
        </div>
    );
};

export default TasksTab;